/**
 * GA4-style e-commerce dataLayer helpers.
 * All events push into `window.dataLayer` so GTM (GTM-PFXLD26R) can forward them
 * to GA4, Meta Pixel, or any other tag configured in the container.
 *
 * Events follow Google's official GA4 e-commerce schema so a standard GA4
 * Ecommerce tag can pick them up with zero re-mapping:
 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 */

const parseGbp = (s) => {
  const m = String(s || "").match(/([\d,]+(\.\d+)?)/);
  return m ? parseFloat(m[1].replace(/,/g, "")) : 0;
};

function push(payload) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

// Convert a product from the API into the GA4 item schema used by view_item / add_to_cart.
function itemFrom(product, quantity = 1) {
  return {
    item_id: product.id,
    item_name: product.name,
    item_brand: "Luxury Hair by DEMJ",
    item_category: product.category || "Luxury Wigs",
    price: parseGbp(product.is_on_sale && product.sale_price_gbp ? product.sale_price_gbp : product.price_gbp),
    quantity,
  };
}

export function trackPageView(page_path) {
  push({ event: "page_view", page_path: page_path || (typeof window !== "undefined" ? window.location.pathname : "/") });
}

export function trackViewItem(product) {
  if (!product) return;
  const item = itemFrom(product);
  push({
    event: "view_item",
    ecommerce: { currency: "GBP", value: item.price, items: [item] },
  });
}

export function trackAddToCart(product, quantity = 1) {
  if (!product) return;
  const item = itemFrom(product, quantity);
  push({
    event: "add_to_cart",
    ecommerce: { currency: "GBP", value: item.price * quantity, items: [item] },
  });
}

export function trackBeginCheckout({ items, subtotalGbp }) {
  push({
    event: "begin_checkout",
    ecommerce: {
      currency: "GBP",
      value: Number(subtotalGbp) || 0,
      items: (items || []).map((i) => ({
        item_id: i.id,
        item_name: i.name,
        item_brand: "Luxury Hair by DEMJ",
        item_category: i.category || "Luxury Wigs",
        price: Number(i.unit_amount_gbp) || 0,
        quantity: i.quantity || 1,
      })),
    },
  });
}

export function trackPurchase({ transaction_id, amount_gbp, subtotal_gbp, shipping_gbp, currency = "GBP", items = [] }) {
  push({
    event: "purchase",
    ecommerce: {
      transaction_id,
      value: Number(amount_gbp) || 0,
      currency,
      shipping: Number(shipping_gbp) || 0,
      tax: 0,
      items: (items || []).map((i) => ({
        item_id: i.collection_id || i.id,
        item_name: i.name,
        item_brand: "Luxury Hair by DEMJ",
        price: Number(i.unit_amount_gbp) || 0,
        quantity: i.quantity || 1,
      })),
    },
    // Convenience — top-level fields for tools that don't unwrap the ecommerce object:
    transaction_id,
    value: Number(amount_gbp) || 0,
    currency,
  });
}
